/*
 * Invoices, the best of Jobber and the old Steward (owner, 2026-10-29). The list: Jobber's Overview with the money next to
 * each status (each one filters), Issued, Average invoice and Payment time against the 30 days before, then Status and Date
 * pills and every invoice with its due day aging in words. One invoice: "Invoice for Jane for $50", the client beside the
 * details, Product / Service with Edit, totals down to the balance, then payments, how they can pay (this invoice only),
 * put it on their other invoice, who worked it, the message to the client and Activity. The Send box shows the exact
 * message and who gets it, Collect payment takes cash and checks, Edit asks Credit or Don't credit when a paid invoice
 * goes down, and Charge saved card is for the owner with a confirm. Payments by day sit on their own page.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  addDays,
  CHECK_WAIT_DAYS,
  chargeQuestion,
  INVOICE_STATE_WORDS,
  invoiceRank,
  KLARNA_MIN_CENTS,
  mergeCheck,
  PAYMENT_METHOD_WORDS,
  type InvoiceDetail,
  type InvoiceFilter,
  type InvoiceRow,
  type InvoiceState,
  type SendPreview,
} from "@steward/shared";
import { Fragment, useEffect, useState, type ReactNode } from "react";
import {
  newKey,
  useNewInvoice,
  useRefund,
  useAutoChargeHold,
  useChargeCard,
  useEditPaid,
  useInvoice,
  useInvoices,
  useMarkSent,
  useMergeInvoices,
  useNoCharge,
  useRecordPayment,
  useSendInvoice,
  useSendPreview,
  useSendReceipt,
  useTakeVisitOff,
  useUpdateInvoice,
  useVoidInvoice,
  type InvoiceLineIn,
  type Via,
} from "../api/money";
import { api } from "../api/client";
import { useClients, usePeople, useTestMode } from "../api/hooks";
import { useSession } from "../app/session";
import { DataTable } from "../components/DataTable";
import { Choice, Combobox, InputText } from "../components/fields";
import { dayDate, followUpText, money, moneyExact, phoneText, plural, shortDate, todayIn, trend } from "../components/format";
import { FilterPick, Menu, Modal, useToast } from "../components/overlays";
import { Icon } from "../components/Icon";
import { PhoneFacts, PhoneRecord } from "../components/PhoneRecord";
import { usePhone } from "../components/usePhone";
import {
  ago,
  Banner,
  Button,
  Card,
  Details,
  EmptyState,
  IconButton,
  LoadError,
  Page,
  SettingRow,
  Skeleton,
  StatusLabel,
  Switch,
  type Status,
} from "../components/parts";
import {
  CrewChips,
  draftLine,
  draftTotal,
  errText,
  LineMore,
  LinesEditor,
  linesOut,
  TeamSheet,
  type DraftLine,
} from "../components/work";
import { SettingsButton } from "./settings/Feature";
import { DisputeBanner } from "./DisputeFile";
import { PhoneCard } from "./PhoneCard";

const STATE_LOOK: Record<InvoiceState, Status> = {
  to_check: "informative",
  awaiting: "warning",
  past_due: "critical",
  debt: "critical",
  paid: "success",
  closed: "inactive",
};
export const InvoiceTag = ({ state }: { state: InvoiceState }) => (
  <StatusLabel status={STATE_LOOK[state]}>{INVOICE_STATE_WORDS[state]}</StatusLabel>
);
const tokenOf = (link: string) => link.split("/").pop() ?? "";
const dollars = (s: string): number | null => {
  const n = Number(s.replace(/[$,\s]/g, ""));
  return s.trim() && Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null;
};

/** An invoice waiting to charge the saved card by itself: when, with Hold (the old autobill). */
/** The client said a check is in the mail: reminders wait until the check comes or 10 days pass. */
function CheckMailBanner({ at, tz }: { at: string; tz: string }) {
  const day = new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(new Date(at));
  return (
    <Banner type="notice">
      Check in the mail since {shortDate(day)}. Reminders wait until {shortDate(addDays(day, CHECK_WAIT_DAYS))}. Use Collect
      payment when it comes.
    </Banner>
  );
}

function AutoChargeBanner({ i }: { i: InvoiceDetail }) {
  const { company, can } = useSession();
  const hold = useAutoChargeHold();
  const toast = useToast();
  const card = i.client.card ? `card ending ${i.client.card.last4}` : "saved card";
  const at = i.autoChargeAt ? new Date(i.autoChargeAt) : null;
  const when =
    at && at.getTime() > Date.now()
      ? `at ${at.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: company.timezone })}`
      : "in a minute";
  const press = (on: boolean) =>
    hold.mutate(
      { id: i.id, hold: on },
      {
        onSuccess: () => toast(on ? "Held. It waits for you." : "It will charge the card in a minute."),
        onError: (e: unknown) => toast(errText(e), { kind: "error" }),
      },
    );
  if (i.autoChargeHeld)
    return (
      <Banner
        type="warning"
        action={
          can("card.charge") ? (
            <Button type="secondary" size="small" loading={hold.isPending} onClick={() => press(false)}>
              Charge it
            </Button>
          ) : null
        }
      >
        Held. The {card} is not charged until you take the hold off or send the invoice.
      </Banner>
    );
  return (
    <Banner
      type="notice"
      action={
        can("invoice.update") ? (
          <Button type="secondary" size="small" loading={hold.isPending} onClick={() => press(true)}>
            Hold
          </Button>
        ) : null
      }
    >
      The {card} is charged {when} for {moneyExact(i.balanceCents)}. Change the price or press Hold first if you need to.
    </Banner>
  );
}

/** The list's date filter, as the old Steward's (09-lists.js LRANGES). */
const RANGES = [
  ["all", "Any time"],
  ["7", "Past 7 days"],
  ["30", "Past 30 days"],
  ["month", "This month"],
  ["lastmonth", "Last month"],
  ["year", "This year"],
] as const;
type Range = (typeof RANGES)[number][0];
function rangeDays(range: Range, today: string): { from?: string; to?: string } {
  const first = `${today.slice(0, 8)}01`;
  if (range === "7" || range === "30") return { from: addDays(today, -Number(range)) };
  if (range === "month") return { from: first };
  if (range === "lastmonth") {
    const last = addDays(first, -1);
    return { from: `${last.slice(0, 8)}01`, to: last };
  }
  if (range === "year") return { from: `${today.slice(0, 4)}-01-01` };
  return {};
}
/** The day a moment falls on in the company's time zone. */
const dayIn = (tz: string, iso: string) => new Intl.DateTimeFormat("en-CA", { timeZone: tz }).format(new Date(iso));
const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);

/**
 * When an invoice is due, aging in words (owner rule 4a, things age visibly): "Oct 30", "Today", "3 days late". Late only
 * while money is still owed; paid and closed ones show the day quietly.
 */
function dueOf(i: InvoiceRow, tz: string, today: string): { text: string; late: boolean } {
  if (!i.dueAt) return { text: i.state === "to_check" ? "Set when sent" : "Upon receipt", late: false };
  const day = dayIn(tz, i.dueAt);
  const open = i.balanceCents > 0 && (i.state === "awaiting" || i.state === "past_due" || i.state === "debt");
  const late = open ? daysBetween(day, today) : 0;
  if (late > 0) return { text: `${plural(late, "day")} late`, late: true };
  if (open && day === today) return { text: "Today", late: false };
  return { text: shortDate(day), late: false };
}

/**
 * The status words an invoice shows, like the old Steward's: To collect, Scheduled and Payment on the way first. One dot
 * for the status; Viewed and Not sent yet are small tinted flags after it, only while money is still owed.
 */
export function InvoiceStatus({ i }: { i: InvoiceRow }) {
  const open = i.state === "awaiting" || i.state === "past_due";
  return (
    <span className="chips tight">
      {i.status === "to_collect" ? (
        <StatusLabel status="warning">To collect</StatusLabel>
      ) : i.status === "scheduled" ? (
        <StatusLabel status="informative">Scheduled</StatusLabel>
      ) : i.onTheWay && open ? (
        <StatusLabel status="informative">Payment on the way</StatusLabel>
      ) : (
        <InvoiceTag state={i.state} />
      )}
      {i.notSent && open ? <span className="status flag orange">Not sent yet</span> : null}
      {i.viewed && open ? <span className="status flag informative">Viewed</span> : null}
    </span>
  );
}

type Pick = InvoiceFilter | "all";

/**
 * The top cards. Overview is Jobber's (a dot, the count and the money for each status) with the old Steward's lines (Bills
 * at month end, To collect, Payment on the way), each one filtering the list, and what is waiting on payment under it.
 * Issued, Average invoice and Payment time are Jobber's, each against the 30 days before so a slow month shows.
 */
function InvoiceTiles({ companyId, tab, setTab }: { companyId: string; tab: Pick; setTab: (t: Pick) => void }) {
  const sum = useInvoices(companyId, { state: "all" }).data?.summary;
  const wait = <span className="skeleton inline" />;
  const lines: { key: InvoiceFilter | null; words: string; n: number; cents: number | null; look: Status }[] = sum
    ? [
        { key: "to_check", words: "To check", n: sum.toCheck, cents: sum.toCheckCents, look: "informative" },
        ...(sum.monthEnd
          ? [{ key: null, words: "Bills at month end", n: sum.monthEnd, cents: null, look: "inactive" as Status }]
          : []),
        { key: "to_collect", words: "To collect", n: sum.toCollect, cents: sum.toCollectCents, look: "warning" },
        { key: "awaiting", words: "Awaiting payment", n: sum.awaiting, cents: sum.awaitingCents, look: "warning" },
        ...(sum.onTheWay
          ? [
              {
                key: "on_the_way" as InvoiceFilter,
                words: "Payment on the way",
                n: sum.onTheWay,
                cents: sum.onTheWayCents,
                look: "informative" as Status,
              },
            ]
          : []),
        { key: "past_due", words: "Past due", n: sum.pastDue, cents: sum.pastDueCents, look: "critical" },
      ]
    : [];
  const avg = sum?.issuedCount ? Math.round(sum.issuedCents / sum.issuedCount) : null;
  const avgBefore = sum?.issuedBeforeCount ? Math.round(sum.issuedBeforeCents / sum.issuedBeforeCount) : null;
  const issued = sum ? trend(sum.issuedCount, sum.issuedBeforeCount) : null;
  const average = avg !== null && avgBefore !== null ? trend(avg, avgBefore) : null;
  const payTime =
    typeof sum?.payDays === "number" && typeof sum.payDaysBefore === "number"
      ? trend(sum.payDays, sum.payDaysBefore, { lowerIsBetter: true })
      : null;
  return (
    <div className="tiles inv-tiles">
      <div className="tile">
        <span className="label">Overview</span>
        {sum ? (
          <div className="ov-rows">
            {lines.map((o) => {
              const k = o.key;
              const words = (
                <>
                  <i className={`ov-dot ${o.look}`} aria-hidden="true" />
                  {o.words} ({o.n})
                </>
              );
              return (
                <Fragment key={o.words}>
                  {k ? (
                    <button type="button" aria-pressed={tab === k} onClick={() => setTab(tab === k ? "all" : k)}>
                      {words}
                    </button>
                  ) : (
                    <span>{words}</span>
                  )}
                  <span className="num muted">{o.cents !== null && o.n ? money(o.cents) : ""}</span>
                </Fragment>
              );
            })}
          </div>
        ) : (
          <Skeleton rows={4} />
        )}
        {sum ? (
          <span className="hint">
            {money(sum.owedCents)} waiting on payment, {plural(sum.owedCount, "invoice")} sent
          </span>
        ) : null}
      </div>
      <div className="tile">
        <span className="label">Issued</span>
        <span className="value">{sum ? sum.issuedCount : wait}</span>
        <span className="hint">{sum ? `Past 30 days, ${moneyExact(sum.issuedCents)}` : ""}</span>
        {issued ? <span className={`hint ${issued.cls}`}>{issued.text}</span> : null}
      </div>
      <div className="tile">
        <span className="label">Average invoice</span>
        <span className="value">{sum ? (avg === null ? "None yet" : moneyExact(avg)) : wait}</span>
        <span className="hint">Past 30 days</span>
        {average ? <span className={`hint ${average.cls}`}>{average.text}</span> : null}
      </div>
      <div className="tile">
        <span className="label">Payment time</span>
        <span className="value">
          {sum ? (sum.payDays === null ? "None yet" : `${String(sum.payDays)} ${sum.payDays === 1 ? "day" : "days"}`) : wait}
        </span>
        <span className="hint">From sent to paid, past 30 days</span>
        {payTime ? <span className={`hint ${payTime.cls}`}>{payTime.text}</span> : null}
      </div>
    </div>
  );
}

/**
 * Send many To check invoices in one go (owner, 2026-10-31: "I can't just go through a bunch and send"). Each goes to the
 * client's own number or email, the one the Send box picks first; one with no way to reach them, or a warning like a
 * number another client shares, stays in To check to open and send by hand.
 */
function SendMany({ ids, onClose }: { ids: string[] | "all"; onClose: (sent: boolean) => void }) {
  const { company } = useSession();
  const list = useInvoices(company.id, { state: "to_check" });
  const send = useSendInvoice();
  const all = (list.data?.invoices ?? []).filter((r) => r.status === "to_check");
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const on = picked ?? new Set(ids === "all" ? all.map((r) => r.id) : ids);
  const chosen = all.filter((r) => on.has(r.id));
  const [via, setVia] = useState<Via>("text");
  const [run, setRun] = useState<{ done: number; sent: number; total: number; left: { name: string; why: string }[] } | null>(
    null,
  );
  const busy = run !== null && run.done < run.total;
  const toggle = (id: string) => {
    const next = new Set(on);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };
  const go = async () => {
    const left: { name: string; why: string }[] = [];
    const list = [...chosen];
    let sent = 0;
    setRun({ done: 0, sent, total: list.length, left });
    for (const [n, r] of list.entries()) {
      try {
        const p = await api<SendPreview>("GET", `invoices/${r.id}/send?via=${via}`);
        const to = p.to ?? p.choices[0];
        if (!to) left.push({ name: r.clientName, why: via === "text" ? "No phone number" : "No email" });
        else if (p.warnings.length) left.push({ name: r.clientName, why: p.warnings[0] ?? "Needs a look" });
        else {
          await send.mutateAsync({ id: r.id, via, to });
          sent += 1;
        }
      } catch (e) {
        left.push({ name: r.clientName, why: errText(e, "That one didn't send.") });
      }
      setRun({ done: n + 1, sent, total: list.length, left: [...left] });
    }
  };
  const total = chosen.reduce((t, r) => t + r.totalCents - r.tipCents, 0);
  return (
    <Modal
      title="Check and send"
      open
      size="full"
      onClose={() => (busy ? undefined : onClose(run !== null))}
      footer={
        run ? (
          <Button disabled={busy} onClick={() => onClose(true)}>
            Done
          </Button>
        ) : (
          <>
            <Button type="tertiary" onClick={() => onClose(false)}>
              Cancel
            </Button>
            <Button icon="mail" disabled={!chosen.length} onClick={() => void go()}>
              {chosen.length ? `Send ${plural(chosen.length, "invoice")}` : "Pick invoices to send"}
            </Button>
          </>
        )
      }
    >
      {run ? (
        <div className="send-many-run" aria-live="polite">
          <b>{busy ? `Sending ${String(run.done + 1)} of ${String(run.total)}` : `${plural(run.sent, "invoice")} sent`}</b>
          <div className="send-many-bar">
            <span style={{ width: `${String(Math.round((run.done / Math.max(1, run.total)) * 100))}%` }} />
          </div>
          {run.left.length ? (
            <>
              <p className="muted small">These stay in To check. Open each one to send it.</p>
              <div className="todo">
                {run.left.map((l, k) => (
                  <div className="item" key={`${l.name}-${String(k)}`}>
                    <div className="what">
                      <b>{l.name}</b>
                      <span>{l.why}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>
      ) : (
        <div className="stack">
          <Choice<Via>
            label="Send by"
            value={via}
            onChange={setVia}
            options={[
              { value: "text", label: "Text" },
              { value: "email", label: "Email, with the PDF" },
            ]}
          />
          {list.isLoading ? (
            <Skeleton rows={4} />
          ) : all.length ? (
            <>
              <div className="row between">
                <span className="muted small">
                  {plural(chosen.length, "invoice")} · {moneyExact(total)}
                </span>
                <Button
                  type="tertiary"
                  size="small"
                  onClick={() => setPicked(chosen.length === all.length ? new Set() : new Set(all.map((r) => r.id)))}
                >
                  {chosen.length === all.length ? "Untick all" : "Tick all"}
                </Button>
              </div>
              <div className="todo send-many-list">
                {all.map((r) => (
                  <label className="item" key={r.id}>
                    <input type="checkbox" checked={on.has(r.id)} onChange={() => toggle(r.id)} />
                    <div className="what">
                      <b>{r.clientName}</b>
                      <span>
                        {[r.subject, r.firstServiceDate ? shortDate(r.firstServiceDate) : null].filter(Boolean).join(" · ")}
                      </span>
                    </div>
                    <span className="num">{moneyExact(r.totalCents - r.tipCents)}</span>
                  </label>
                ))}
              </div>
            </>
          ) : (
            <p className="muted">Nothing is waiting in To check.</p>
          )}
        </div>
      )}
    </Modal>
  );
}

/** The list, filling the screen like the old Steward's (owner, 2026-10-29), with Jobber's top cards and filter pills. */
export function InvoicesList() {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const today = todayIn(company.timezone);
  const [tab, setTab] = useState<Pick>("all");
  const [range, setRange] = useState<Range>("all");
  const [typed, setTyped] = useState("");
  const list = useInvoices(company.id, {
    state: tab,
    ...rangeDays(range, today),
    ...(typed.trim() ? { q: typed.trim() } : {}),
  });
  const counts = list.data?.counts;
  const sum = list.data?.summary;
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const merge = useMergeInvoices();
  const toast = useToast();
  const pickedRows = (list.data?.invoices ?? []).filter((r) => picked.has(r.id));
  const mergeWhy = mergeHint(pickedRows, can("invoice.edit_paid"));
  const [making, setMaking] = useState<false | { clientId: string }>(false);
  const [sendMany, setSendMany] = useState<string[] | "all" | null>(null);
  const toSend = pickedRows.filter((r) => r.status === "to_check");
  const toCheckN = counts?.to_check ?? 0;
  // Create, Invoice in the side menu lands here with ?new=true (and a client page's + with its clientId): open the box,
  // then drop it from the address.
  const search = useSearch({ strict: false });
  useEffect(() => {
    if (!search.new) return;
    setMaking({ clientId: search.clientId ?? "" });
    void navigate({ to: "/invoices", search: {}, replace: true });
  }, [search.new, search.clientId, navigate]);
  const picks: { value: Pick; label: string; count?: number }[] = [
    { value: "all", label: "All" },
    ...(
      [
        ["to_check", "To check", counts?.to_check],
        ["to_collect", "To collect", sum?.toCollect],
        ["awaiting", "Awaiting payment", counts?.awaiting],
        ...(sum?.onTheWay ? [["on_the_way", "Payment on the way", sum.onTheWay] as const] : []),
        ["past_due", "Past due", counts?.past_due],
        ["debt", "Debt", counts?.debt],
        ["paid", "Paid", counts?.paid],
        ["closed", "Archived", counts?.closed],
      ] as const
    ).map(([value, label, n]) => ({ value, label, ...(n !== undefined ? { count: n } : {}) })),
  ];
  const filtered = tab !== "all" || range !== "all" || typed.trim() !== "";
  return (
    <Page
      title="Invoices"
      secondaryAction={<SettingsButton to="/invoices/settings">Invoice settings</SettingsButton>}
      primaryAction={
        can("invoice.update") ? (
          <Button icon="plus" onClick={() => setMaking({ clientId: "" })}>
            New invoice
          </Button>
        ) : null
      }
    >
      {making ? (
        <NewInvoice
          clientId={making.clientId}
          onClose={() => setMaking(false)}
          onMade={(id, send) =>
            void navigate({ to: "/invoices/$id", params: { id }, search: send ? { then: "send" as const } : {} })
          }
        />
      ) : null}
      {sendMany ? (
        <SendMany
          ids={sendMany}
          onClose={(sent) => {
            setSendMany(null);
            if (sent) setPicked(new Set());
          }}
        />
      ) : null}
      <InvoiceTiles companyId={company.id} tab={tab} setTab={setTab} />
      <DataTable<InvoiceRow>
        title="All invoices"
        rows={list.data?.invoices ?? []}
        rowKey={(i) => i.id}
        loading={list.isFetching}
        error={list.error ? <LoadError error={list.error} retry={() => void list.refetch()} /> : null}
        query={{ value: typed, onChange: setTyped, placeholder: "Search invoices" }}
        onRowClick={(i) => void navigate({ to: "/invoices/$id", params: { id: i.id } })}
        {...(can("invoice.update") ? { selected: picked, onSelect: setPicked } : {})}
        bulk={
          <>
            {can("invoice.send") && toSend.length ? (
              <Button size="small" icon="mail" onClick={() => setSendMany(toSend.map((r) => r.id))}>
                {`Send ${String(toSend.length)}`}
              </Button>
            ) : null}
            {mergeWhy ? <span className="muted small">{mergeWhy}</span> : null}
            <Button
              size="small"
              icon="merge"
              disabled={mergeWhy !== null}
              loading={merge.isPending}
              onClick={() =>
                merge.mutate([...picked], {
                  onSuccess: (d) => {
                    setPicked(new Set());
                    toast(`Put on one invoice${d.number ? ` #${String(d.number)}` : ""}`);
                    void navigate({ to: "/invoices/$id", params: { id: d.id } });
                  },
                  onError: (e) => toast(errText(e), { kind: "error" }),
                })
              }
            >
              Merge
            </Button>
          </>
        }
        tools={
          <div className="chips">
            {can("invoice.send") && toCheckN ? (
              <Button size="small" icon="mail" onClick={() => setSendMany("all")}>
                {`Check and send ${String(toCheckN)}`}
              </Button>
            ) : null}
            <FilterPick<Pick> label="Status" value={tab} onChange={setTab} options={picks} />
            <FilterPick<Range>
              label="Date"
              value={range}
              onChange={setRange}
              options={RANGES.map(([value, label]) => ({ value, label }))}
            />
          </div>
        }
        empty={
          <EmptyState icon="invoices" title={filtered ? "No invoices match" : "No invoices yet"}>
            {filtered
              ? "Try another status, time or search."
              : "When a visit is marked done it lands here in To check. Check it, then send it."}
          </EmptyState>
        }
        phoneCard={(i) => {
          // Jobber's phone list (owner shot, 2026-10-31): name and status, then "Oct 21 | $300 | Cleaning Services".
          const issued = i.sentAt ? dayIn(company.timezone, i.sentAt) : i.firstServiceDate;
          return (
            <div className="row-card with-icon">
              <Icon name="invoices" />
              <div>
                <div className="top">
                  <b>{i.clientName}</b>
                  <InvoiceStatus i={i} />
                </div>
                <span className="sub">
                  {[issued ? shortDate(issued) : null, money(i.totalCents - i.tipCents), i.subject || null]
                    .filter(Boolean)
                    .join(" | ")}
                </span>
              </div>
            </div>
          );
        }}
        columns={[
          {
            key: "client",
            header: "Client",
            sort: (i) => i.clientName.toLowerCase(),
            render: (i) => <b>{i.clientName}</b>,
          },
          {
            key: "number",
            header: "Invoice number",
            sort: (i) => i.number ?? 0,
            render: (i) => <span className="num">{i.number ? `#${String(i.number)}` : "-"}</span>,
          },
          {
            key: "date",
            header: "Issued",
            sort: (i) => i.sentAt ?? i.firstServiceDate ?? "",
            render: (i) => {
              const d = i.sentAt ? dayIn(company.timezone, i.sentAt) : i.firstServiceDate;
              return d ? shortDate(d) : "-";
            },
          },
          {
            key: "due",
            header: "Due",
            sort: (i) => i.dueAt ?? "",
            render: (i) => {
              if (i.state === "to_check") return <span className="muted">-</span>;
              const d = dueOf(i, company.timezone, today);
              return <span className={d.late ? "late-words" : i.balanceCents > 0 ? undefined : "muted"}>{d.text}</span>;
            },
          },
          {
            key: "subject",
            header: "Subject",
            sort: (i) => i.subject.toLowerCase(),
            render: (i) => (
              <span className="muted">
                {i.subject || "-"}
                {i.visitCount > 1 ? ` · ${plural(i.visitCount, "visit")}` : ""}
              </span>
            ),
          },
          { key: "state", header: "Status", sort: invoiceRank, render: (i) => <InvoiceStatus i={i} /> },
          {
            key: "total",
            header: "Total",
            align: "right",
            sort: (i) => i.totalCents - i.tipCents,
            render: (i) => (
              <span className="num money-cell">
                <b>{moneyExact(i.totalCents - i.tipCents)}</b>
                {i.tipCents > 0 ? <span className="tip-note">Tip {moneyExact(i.tipCents)}</span> : null}
              </span>
            ),
          },
          {
            key: "balance",
            header: "Balance",
            align: "right",
            sort: (i) => i.balanceCents,
            render: (i) => <span className="num">{moneyExact(i.balanceCents)}</span>,
          },
        ]}
      />
    </Page>
  );
}

// One invoice --------------------------------------------------------------------------------------------------------

type Open = "send" | "collect" | "edit" | "charge" | "receipt" | "void" | "no_charge" | null;

export function InvoicePage({ id, then }: { id: string; then?: "send" }) {
  const [refunding, setRefunding] = useState<string | null>(null);
  const { can, company } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const inv = useInvoice(id);
  const markSent = useMarkSent();
  const update = useUpdateInvoice();
  const [open, setOpen] = useState<Open>(then === "send" ? "send" : null);
  const [via, setVia] = useState<Via>("text");
  const [phoneTab, setPhoneTab] = useState<"invoice" | "activity">("invoice");
  const phone = usePhone();
  const i = inv.data;
  if (inv.error)
    return (
      <Page title="Invoice">
        <LoadError error={inv.error} retry={() => void inv.refetch()} />
      </Page>
    );
  if (!i)
    return (
      <Page title={<span className="skeleton inline wide" />}>
        <Skeleton rows={6} />
      </Page>
    );
  const today = todayIn(company.timezone);
  const toCheck = i.state === "to_check";
  const closed = i.status === "void" || i.status === "no_charge";
  const owes = i.balanceCents > 0 && !closed;
  const card = i.client.card;
  const editable = !closed && (i.paidCents > 0 ? can("invoice.edit_paid") : can("invoice.update"));
  const done = (text: string) => ({
    onSuccess: () => toast(text),
    onError: (e: unknown) => toast(errText(e), { kind: "error" }),
  });
  const close = () => setOpen(null);
  const send = (how: Via) => {
    setVia(how);
    setOpen("send");
  };
  const due = dueOf(i, company.timezone, today);
  const details: [ReactNode, ReactNode][] = [
    ["Invoice number", i.number ? `#${String(i.number)}` : "Given when it is sent"],
    ["Issued", i.issuedOn ? shortDate(i.issuedOn) : "Not sent yet"],
    [
      "Due",
      due.late ? (
        <span className="late-words">{`${shortDate(dayIn(company.timezone, i.dueAt ?? ""))}, ${due.text}`}</span>
      ) : (
        due.text
      ),
    ],
    [
      "Last sent",
      i.sentAt ? `${shortDate(dayIn(company.timezone, i.sentAt))}${i.sentTo ? ` to ${i.sentTo}` : ""}` : "Not sent yet",
    ],
    ...(i.sentAt
      ? ([["Viewed", i.viewedAt ? `${shortDate(dayIn(company.timezone, i.viewedAt))}, ${ago(i.viewedAt)}` : "Not yet"]] as [
          string,
          string,
        ][])
      : []),
    ...(i.paidAt && i.status === "paid" ? ([["Paid", shortDate(dayIn(company.timezone, i.paidAt))]] as [string, string][]) : []),
    ...(i.visits.length ? ([["Visits", i.visits.map((v) => shortDate(v.date)).join(", ")]] as [string, string][]) : []),
    ...(i.isBusiness ? ([["PO number", <PoNumber key="po" i={i} />]] as [string, ReactNode][]) : []),
  ];
  const primary = toCheck ? (
    can("invoice.send") ? (
      <Button icon="mail" onClick={() => send("text")}>
        Confirm and send
      </Button>
    ) : null
  ) : owes && can("payment.record") ? (
    <Button icon="money" onClick={() => setOpen("collect")}>
      Collect payment
    </Button>
  ) : null;
  const sendAgain = (look: "primary" | "secondary" = "primary") =>
    !can("invoice.send") || closed || toCheck ? null : i.status === "paid" ? (
      <Button type={look} icon="mail" onClick={() => setOpen("receipt")}>
        Send receipt
      </Button>
    ) : (
      <Button type={look} icon="mail" onClick={() => send("text")}>
        Resend
      </Button>
    );
  const secondary = sendAgain("secondary");
  const moreMenu = (
    <Menu align="right" button={({ toggle }) => <IconButton icon="more" label="More" onClick={toggle} />}>
      {(closeMenu) => {
        const item = (label: string, run: () => void, show = true, danger = false) =>
          show ? (
            <button
              key={label}
              type="button"
              role="menuitem"
              className={danger ? "danger" : undefined}
              onClick={() => {
                closeMenu();
                run();
              }}
            >
              {label}
            </button>
          ) : null;
        const canSend = can("invoice.send") && !closed && i.status !== "paid";
        const canClose = i.paidCents === 0 && !closed && i.status !== "paid" && can("invoice.update");
        return (
          <>
            {canSend ? <span className="menu-label">Send as</span> : null}
            {item("Text message", () => send("text"), canSend)}
            {item("Email", () => send("email"), canSend)}
            {canSend ? <hr /> : null}
            {item("Edit line items", () => setOpen("edit"), editable)}
            {item(
              "Preview as the client sees it",
              () => void navigate({ to: "/pay/$token", params: { token: tokenOf(i.payLink) } }),
              !toCheck,
            )}
            {item(
              "Copy pay link",
              () => {
                void navigator.clipboard.writeText(i.payLink);
                toast("Pay link copied");
              },
              !toCheck,
            )}
            {item("Print or save PDF", () => window.open(`/v1/invoices/${i.id}/pdf`, "_blank"), !__PREVIEW__)}
            {item("Send receipt", () => setOpen("receipt"), i.status === "paid" && can("invoice.send"))}
            {/* A To check invoice too (owner, Oct 31): the client already OKd it, so the card on file can be charged now. */}
            {item("Charge saved card", () => setOpen("charge"), Boolean(card) && owes && can("card.charge"))}
            {item(
              "Mark as sent",
              () => markSent.mutate(i.id, done("Marked as sent. Nothing was sent to the client.")),
              toCheck && can("invoice.send"),
            )}
            {item(
              i.checkMailedAt ? "Check not coming" : "Check in the mail",
              () =>
                update.mutate(
                  { id: i.id, patch: { checkMailed: !i.checkMailedAt } },
                  done(
                    i.checkMailedAt ? "Reminders are back on" : `Reminders wait ${String(CHECK_WAIT_DAYS)} days for the check`,
                  ),
                ),
              owes && !toCheck && i.status !== "to_collect" && can("invoice.update"),
            )}
            {item(
              i.debt ? "Take out of Debt" : "Mark as debt",
              () => update.mutate({ id: i.id, patch: { debt: !i.debt } }, done(i.debt ? "Taken out of Debt" : "Marked as debt")),
              owes && !toCheck && can("invoice.update"),
            )}
            {canClose ? <hr /> : null}
            {item("No charge", () => setOpen("no_charge"), canClose)}
            {item("Void invoice", () => setOpen("void"), canClose, true)}
          </>
        );
      }}
    </Menu>
  );
  const banners = (
    <>
      <DisputeBanner invoiceId={i.id} clientId={i.clientId} />
      {i.payNote ? <Banner type="warning">{i.payNote}</Banner> : null}
      {i.checkMailedAt && owes ? <CheckMailBanner at={i.checkMailedAt} tz={company.timezone} /> : null}
      {i.status === "to_collect" && (i.autoChargeAt || i.autoChargeHeld) ? <AutoChargeBanner i={i} /> : null}
      {i.client.creditCents > 0 && owes ? (
        <Banner type="notice">
          {i.client.name} has {moneyExact(i.client.creditCents)} in account credit. It is used when this invoice is first sent.
        </Banner>
      ) : null}
    </>
  );
  const linesCard = (
    <Card
      title="Product / Service"
      action={
        editable ? (
          <Button type="tertiary" size="small" icon="edit" onClick={() => setOpen("edit")}>
            Edit
          </Button>
        ) : null
      }
      flush
    >
      <InvoiceLines i={i} />
    </Card>
  );
  const paymentsCard = (
    <>
      {refunding ? <RefundBox i={i} paymentId={refunding} onClose={() => setRefunding(null)} /> : null}
      <Card title="Payments" flush>
        {i.payments.length ? (
          <table className="data pay-rows">
            <tbody>
              {i.payments.map((p) => (
                <tr
                  key={p.id}
                  className="clickable"
                  onClick={(e) =>
                    (e.target as HTMLElement).closest("button")
                      ? undefined
                      : void navigate({ to: "/payments/$id", params: { id: p.id } })
                  }
                >
                  <td>{shortDate(dayIn(company.timezone, p.receivedAt))}</td>
                  <td>
                    {PAYMENT_METHOD_WORDS[p.method]}
                    {p.checkNumber ? ` #${p.checkNumber}` : ""}
                    {p.note ? <span className="muted"> · {p.note}</span> : null}
                  </td>
                  <td className="muted">{p.recordedBy ?? "Client"}</td>
                  <td className="right num">{moneyExact(p.amountCents)}</td>
                  <td className="right">
                    {can("card.charge") && p.amountCents > 0 ? (
                      <Button type="tertiary" size="small" onClick={() => setRefunding(p.id)}>
                        Refund
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="card-empty muted">
            {owes ? "No payments yet. Card and bank payments land here by themselves." : "No payments."}
          </div>
        )}
      </Card>
    </>
  );
  const sideCards = (
    <>
      {i.mergeWith.length && can("invoice.update") ? <MergeCard i={i} /> : null}
      {takeOffShows(i) && can("invoice.update") ? <TakeOffCard i={i} /> : null}
      {owes ? <PayWaysCard i={i} /> : null}
      {i.visits.length ? null : <WhoWorked i={i} />}
      <ClientMessage i={i} />
    </>
  );
  const sheets = (
    <>
      <SendBox key={`send-${via}`} i={i} via={via} open={open === "send"} onClose={close} />
      {open === "receipt" ? <ReceiptBox i={i} onClose={close} /> : null}
      {open === "void" || open === "no_charge" ? <CloseInvoice i={i} how={open} onClose={close} /> : null}
      <CollectPayment i={i} open={open === "collect"} onClose={close} />
      <EditLines key={`${i.id}-${String(open === "edit")}`} i={i} open={open === "edit"} onClose={close} />
      <ChargeCard i={i} open={open === "charge"} onClose={close} />
    </>
  );

  // Like Jobber's phone invoice (owner, 2026-10-30): "Invoice for X for $", Issued and Due, one big button and More,
  // Invoice and Activity tabs, payments inside the totals, the client at the bottom.
  if (phone)
    return (
      <PhoneRecord
        back={{ label: "Back", go: () => void navigate({ to: "/invoices" }) }}
        kind="invoices"
        status={
          <>
            <InvoiceStatus i={i} />
            <span className="muted num">{i.number ? `#${String(i.number)}` : null}</span>
          </>
        }
        title={`Invoice for ${i.clientName} for ${moneyExact(i.totalCents)}`}
        sub={i.subject || null}
        phone={i.client.phone}
        email={i.client.email}
        meta={[
          { label: "Issued", value: i.issuedOn ? shortDate(i.issuedOn) : "Not sent yet" },
          {
            label: "Due",
            value: due.late ? (
              <span className="late-words">{`${shortDate(dayIn(company.timezone, i.dueAt ?? ""))}, ${due.text}`}</span>
            ) : (
              due.text
            ),
          },
        ]}
        actions={
          <>
            {primary ?? sendAgain()}
            {moreMenu}
          </>
        }
        tabs={{
          tabs: [
            { key: "invoice", label: "Invoice" },
            { key: "activity", label: "Activity" },
          ],
          active: phoneTab,
          onChange: (t) => setPhoneTab(t === "activity" ? "activity" : "invoice"),
        }}
      >
        {phoneTab === "activity" ? (
          <InvoiceActivity i={i} />
        ) : (
          <>
            {banners}
            <Card
              title="Product / Service"
              action={
                editable ? (
                  <Button type="tertiary" size="small" icon="edit" onClick={() => setOpen("edit")}>
                    Edit
                  </Button>
                ) : null
              }
              flush
            >
              <InvoiceLines
                i={i}
                onPayment={(pid) => void navigate({ to: "/payments/$id", params: { id: pid } })}
                timezone={company.timezone}
              />
            </Card>
            {sideCards}
            <Card title="Overview">
              <Details rows={details} />
            </Card>
            <Card flush>
              <PhoneFacts
                facts={[
                  {
                    key: "client",
                    icon: "person",
                    main: i.clientName,
                    sub: i.client.address ?? undefined,
                    onClick: () => void navigate({ to: "/clients/$id", params: { id: i.clientId } }),
                  },
                  {
                    key: "card",
                    icon: "lock",
                    main: card ? `${card.brand} ending ${card.last4}` : "No saved card",
                    sub: [
                      i.client.autoCharge ? "Auto charge on" : "Auto charge off",
                      i.client.creditCents ? `${moneyExact(i.client.creditCents)} credit` : null,
                    ]
                      .filter(Boolean)
                      .join(" · "),
                    ...(card && owes && can("card.charge") ? { onClick: () => setOpen("charge") } : {}),
                  },
                ]}
              />
            </Card>
          </>
        )}
        {sheets}
      </PhoneRecord>
    );

  return (
    <Page
      title={
        <>
          Invoice for{" "}
          <button
            type="button"
            className="link title-link"
            onClick={() => void navigate({ to: "/clients/$id", params: { id: i.clientId } })}
          >
            {i.clientName}
          </button>{" "}
          for {moneyExact(i.totalCents)}
        </>
      }
      titleMetaData={<InvoiceStatus i={i} />}
      subtitle={i.subject || null}
      primaryAction={primary}
      secondaryAction={secondary}
      moreActions={moreMenu}
    >
      {banners}
      <div className="inv-top">
        <ClientCard i={i} />
        <Card title="Details">
          <Details rows={details} />
        </Card>
      </div>
      {linesCard}
      <div className="home-grid">
        <div className="stack">
          {paymentsCard}
          <InvoiceActivity i={i} />
        </div>
        <div className="stack">{sideCards}</div>
      </div>
      {sheets}
    </Page>
  );
}

/** Who the invoice is for, beside the details (Jobber): their address, phone, email, saved card and credit. */
function ClientCard({ i }: { i: InvoiceDetail }) {
  const navigate = useNavigate();
  const c = i.client;
  const phone = phoneText(c.phone);
  return (
    <Card
      title="Client"
      action={
        <Button type="tertiary" size="small" onClick={() => void navigate({ to: "/clients/$id", params: { id: c.id } })}>
          Open client
        </Button>
      }
    >
      <div className="stack">
        <b>{c.name}</b>
        <Details
          rows={[
            ["Address", c.address],
            ["Phone", phone && c.phone ? <a href={`tel:${c.phone}`}>{phone}</a> : null],
            ["Email", c.email ? <a href={`mailto:${c.email}`}>{c.email}</a> : null],
            ["Saved card", c.card ? `${c.card.brand} ending ${c.card.last4}` : "None"],
            ["Account credit", c.creditCents ? moneyExact(c.creditCents) : "None"],
            ["Auto charge", c.autoCharge ? "On, repeating jobs only" : "Off"],
          ]}
        />
      </div>
    </Card>
  );
}

/** Jobber's line items (name, note, quantity, unit price, total) with the old Steward's day of the work over each line. */
/** onPayment (phone): the payments sit inside the totals like Jobber's, each one opening its payment page. */
function InvoiceLines({ i, onPayment, timezone }: { i: InvoiceDetail; onPayment?: (id: string) => void; timezone?: string }) {
  return (
    <>
      <table className="data lines">
        <thead className="lines-head">
          <tr>
            <th>Line item</th>
            <th className="right">Quantity</th>
            <th className="right">Unit price</th>
            <th className="right">Total</th>
          </tr>
        </thead>
        <tbody>
          {i.lines.map((l) => (
            <tr key={l.id}>
              <td>
                {l.serviceDate || l.propertyLabel ? (
                  <div className="muted small">
                    {[l.serviceDate ? dayDate(l.serviceDate) : null, l.propertyLabel].filter(Boolean).join(" · ")}
                  </div>
                ) : null}
                <b>{l.description}</b>
                <LineMore line={l} basis={false} />
              </td>
              <td className="right num">{l.quantity}</td>
              <td className="right num muted">{moneyExact(l.unitPriceCents)}</td>
              <td className="right num">{moneyExact(l.amountCents)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="inv-totals">
        <dl>
          <div>
            <dt>Subtotal</dt>
            <dd className="num">{moneyExact(i.subtotalCents)}</dd>
          </div>
          {i.tipCents ? (
            <div>
              <dt>Tip</dt>
              <dd className="num">{moneyExact(i.tipCents)}</dd>
            </div>
          ) : null}
          <div className="total">
            <dt>Total</dt>
            <dd className="num">{moneyExact(i.totalCents)}</dd>
          </div>
          {onPayment && timezone && i.payments.length ? (
            i.payments.map((p) => (
              <div
                key={p.id}
                className="pay-line"
                role="button"
                tabIndex={0}
                onClick={() => onPayment(p.id)}
                onKeyDown={(e) => (e.key === "Enter" ? onPayment(p.id) : undefined)}
              >
                <dt>
                  {PAYMENT_METHOD_WORDS[p.method]}
                  <span className="muted"> · {shortDate(dayIn(timezone, p.receivedAt))}</span>
                </dt>
                <dd className="num">{p.amountCents < 0 ? `+${moneyExact(-p.amountCents)}` : `-${moneyExact(p.amountCents)}`}</dd>
              </div>
            ))
          ) : i.paidCents ? (
            <div>
              <dt>Paid</dt>
              <dd className="num">-{moneyExact(i.paidCents)}</dd>
            </div>
          ) : null}
        </dl>
        <div className="inv-balance">
          <div>
            <span>Invoice balance</span>
            <b className="num">{moneyExact(Math.max(0, i.balanceCents))}</b>
          </div>
          {i.client.creditCents ? (
            <div className="muted">
              <span>Account credit</span>
              <span className="num">{moneyExact(i.client.creditCents)}</span>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

/** What happened to the invoice, newest first, from its own dates, payments and changes (Jobber's and the old Steward's). */
function InvoiceActivity({ i }: { i: InvoiceDetail }) {
  const when = (iso: string) =>
    new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  const editWords: Record<string, string> = { credit: "credited", no_credit: "not credited", up: "raised" };
  const events: { what: string; at: string }[] = [{ what: "Made", at: i.createdAt }];
  if (i.firstSentAt)
    events.push({ what: `Sent${i.sentAt === i.firstSentAt && i.sentTo ? ` to ${i.sentTo}` : ""}`, at: i.firstSentAt });
  if (i.sentAt && i.sentAt !== i.firstSentAt)
    events.push({ what: `Sent again${i.sentTo ? ` to ${i.sentTo}` : ""}`, at: i.sentAt });
  if (i.viewedAt) events.push({ what: "Opened by the client", at: i.viewedAt });
  for (const f of i.followUps) events.push({ what: followUpText(f), at: f.at });
  for (const p of i.payments)
    events.push({
      what:
        p.amountCents < 0
          ? `${moneyExact(-p.amountCents)} given back`
          : `${moneyExact(p.amountCents)} paid by ${PAYMENT_METHOD_WORDS[p.method].toLowerCase()}${p.recordedBy ? `, written down by ${p.recordedBy}` : ""}`,
      at: p.receivedAt,
    });
  for (const e of i.edits)
    events.push({
      what: `Changed from ${moneyExact(e.fromTotalCents)} to ${moneyExact(e.toTotalCents)}${editWords[e.kind] ? `, ${editWords[e.kind] ?? ""}` : ""}${e.by ? ` by ${e.by}` : ""}`,
      at: e.at,
    });
  if (i.paidAt && i.status === "paid") events.push({ what: "Paid in full", at: i.paidAt });
  events.sort((a, b) => b.at.localeCompare(a.at));
  return (
    <Card title="Activity">
      <ul className="activity-list">
        {i.status === "void" ? (
          <li>
            <span>Voided</span>
          </li>
        ) : i.status === "no_charge" ? (
          <li>
            <span>Closed with no charge</span>
          </li>
        ) : null}
        {events.map((e, n) => (
          <li key={n}>
            <span>{e.what}</span>
            <span className="muted small num">{when(e.at)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Why the ticked rows on the list can't be merged (the server checks again), or null when they can. */
function mergeHint(rows: readonly InvoiceRow[], mayEditSent: boolean): string | null {
  if (rows.length < 2) return "Tick another invoice of the same client.";
  const check = mergeCheck(
    rows.map((r) => ({
      id: r.id,
      clientId: r.clientId,
      status: r.status,
      paidCents: r.paidCents,
      billKind: null,
      quoteId: null,
    })),
    mayEditSent,
  );
  return check.ok ? null : check.message;
}

/**
 * The old Steward's "Put it on Sydney's other invoice" and Add to invoice. From To check, the client's other To check
 * invoices and a sent one not paid yet; from a sent one, the To check ones. Nothing is sent.
 */
function MergeCard({ i }: { i: InvoiceDetail }) {
  const { can } = useSession();
  const merge = useMergeInvoices();
  const navigate = useNavigate();
  const toast = useToast();
  const mayEditSent = can("invoice.edit_paid");
  if (i.status === "sent" && !mayEditSent) return null;
  const options = i.mergeWith.filter((o) => o.status !== "sent" || mayEditSent);
  if (!options.length) return null;
  const many = options.length > 1;
  const sentHere = i.status === "sent";
  return (
    <Card
      title={
        sentHere
          ? `Add ${i.client.firstName}'s waiting ${many ? "invoices" : "invoice"}`
          : `Put it on ${i.client.firstName}'s other ${many ? "invoices" : "invoice"}`
      }
    >
      <div className="stack">
        <p className="muted small">
          The lines go on one invoice, each with its own day. A sent invoice keeps its number and pay link and shows the new
          total. The other one is voided. Nothing is sent.
        </p>
        {options.map((o) => (
          <div className="row between" key={o.id}>
            <span>
              {o.status === "sent" ? <b>{o.number ? `#${String(o.number)} sent, ` : "Sent, "}</b> : null}
              {o.firstServiceDate ? dayDate(o.firstServiceDate) : "No day"}, {moneyExact(o.totalCents)}
            </span>
            <Button
              type="secondary"
              size="small"
              loading={merge.isPending && merge.variables.includes(o.id)}
              onClick={() =>
                merge.mutate([i.id, o.id], {
                  onSuccess: (d) => {
                    toast(o.status === "sent" || sentHere ? "Added to the sent invoice" : "Put on one invoice");
                    if (d.id !== i.id) void navigate({ to: "/invoices/$id", params: { id: d.id } });
                  },
                  onError: (e) => toast(errText(e), { kind: "error" }),
                })
              }
            >
              {o.status === "sent" ? "Add to it" : sentHere ? "Add here" : "Put together"}
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** A visit can come off: more than one visit, in To check or sent and not paid. */
function takeOffShows(i: InvoiceDetail): boolean {
  return i.visits.length > 1 && (i.status === "to_check" || i.status === "sent");
}

/** The old Steward's unmerge: one visit comes off onto a new invoice in To check. Nothing is sent. */
function TakeOffCard({ i }: { i: InvoiceDetail }) {
  const { can } = useSession();
  const off = useTakeVisitOff();
  const navigate = useNavigate();
  const toast = useToast();
  if (i.status === "sent" && !can("invoice.edit_paid")) return null;
  return (
    <Card title="Take a visit off">
      <div className="stack">
        <p className="muted small">Its lines go on a new invoice in To check. This one shows the lower total. Nothing is sent.</p>
        {i.visits.map((v) => (
          <div className="row between" key={v.ref}>
            <span>
              {dayDate(v.date)}, {v.jobName}
            </span>
            <Button
              type="tertiary"
              size="small"
              loading={off.isPending && off.variables.visitId === v.ref}
              onClick={() =>
                off.mutate(
                  { id: i.id, visitId: v.ref },
                  {
                    onSuccess: (d) => {
                      toast("Taken off. It has its own invoice now");
                      void navigate({ to: "/invoices/$id", params: { id: d.id } });
                    },
                    onError: (e) => toast(errText(e), { kind: "error" }),
                  },
                )
              }
            >
              Take off
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

/** The old Steward's "How they can pay", for this invoice only: card always, bank and Klarna on or off. */
function PayWaysCard({ i }: { i: InvoiceDetail }) {
  const { can } = useSession();
  const update = useUpdateInvoice();
  const toast = useToast();
  const w = i.payWays;
  const edit = can("invoice.update");
  const save = (payWays: { card: true; bank: boolean; klarna: boolean } | null) =>
    update.mutate(
      { id: i.id, patch: { payWays } },
      {
        onSuccess: () => toast(payWays ? "Saved for this invoice" : "Back to your settings"),
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  return (
    <Card
      title="How they can pay"
      action={
        w.own && edit ? (
          <Button type="tertiary" size="small" loading={update.isPending} onClick={() => save(null)}>
            Use my settings
          </Button>
        ) : null
      }
    >
      <div className="stack">
        <SettingRow
          title="Card"
          description="Always on, with Apple Pay and Google Pay"
          control={<Switch checked label="Card" disabled onChange={() => undefined} />}
        />
        <SettingRow
          title="Bank (ACH)"
          description="Takes a few days to land"
          control={
            <Switch
              checked={w.bank}
              label="Bank (ACH)"
              disabled={!edit || update.isPending}
              onChange={(bank) => save({ card: true, bank, klarna: w.klarna })}
            />
          }
        />
        <SettingRow
          title="Pay in 4 with Klarna"
          description={
            i.klarnaFits
              ? "Shows on this invoice"
              : `Only for one-time work of ${money(KLARNA_MIN_CENTS)} or more that is not charged by itself`
          }
          control={
            <Switch
              checked={w.klarna && i.klarnaFits}
              label="Pay in 4 with Klarna"
              disabled={!edit || !i.klarnaFits || update.isPending}
              onChange={(klarna) => save({ card: true, bank: w.bank, klarna })}
            />
          }
        />
        <p className="muted small">
          {w.own
            ? "Set for this invoice only. Your settings stay the same."
            : "From your settings. A change here is for this invoice only."}
        </p>
      </div>
    </Card>
  );
}

/** The message on the invoice (the old Steward's Client message): read first, Edit to change it while it is not paid. */
function ClientMessage({ i }: { i: InvoiceDetail }) {
  const { can } = useSession();
  const update = useUpdateInvoice();
  const toast = useToast();
  const [writing, setWriting] = useState(false);
  const [text, setText] = useState(i.noteToClient ?? "");
  const edit = can("invoice.update") && i.status !== "paid" && i.status !== "void" && i.status !== "no_charge";
  if (!i.noteToClient && !edit) return null;
  return (
    <Card
      title="Message to the client"
      action={
        edit && !writing ? (
          <Button type="tertiary" size="small" icon="edit" onClick={() => setWriting(true)}>
            {i.noteToClient ? "Edit" : "Add"}
          </Button>
        ) : null
      }
    >
      {writing ? (
        <div className="stack">
          <textarea
            rows={3}
            value={text}
            aria-label="Message to the client"
            placeholder="Thanks for having us. Call us with any questions."
            onChange={(e) => setText(e.target.value)}
          />
          <div className="row">
            <Button
              size="small"
              loading={update.isPending}
              onClick={() =>
                update.mutate(
                  { id: i.id, patch: { noteToClient: text.trim() || null } },
                  {
                    onSuccess: () => {
                      setWriting(false);
                      toast("Message saved");
                    },
                    onError: (e) => toast(errText(e), { kind: "error" }),
                  },
                )
              }
            >
              Save
            </Button>
            <Button
              type="tertiary"
              size="small"
              onClick={() => {
                setText(i.noteToClient ?? "");
                setWriting(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : i.noteToClient ? (
        <p className="pre-wrap">{i.noteToClient}</p>
      ) : (
        <p className="muted">None. It shows on the invoice and the pay page.</p>
      )}
    </Card>
  );
}

/** Void or No charge, asked once in a popup (money rule: a confirm before it closes). */
function CloseInvoice({ i, how, onClose }: { i: InvoiceDetail; how: "void" | "no_charge"; onClose: () => void }) {
  const voidIt = useVoidInvoice();
  const noCharge = useNoCharge();
  const toast = useToast();
  const busy = voidIt.isPending || noCharge.isPending;
  const after = {
    onSuccess: () => {
      toast(how === "void" ? "Invoice voided" : "Closed with no charge");
      onClose();
    },
    onError: (e: unknown) => toast(errText(e), { kind: "error" }),
  };
  return (
    <Modal
      title={how === "void" ? "Void this invoice?" : "Close with no charge?"}
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            {...(how === "void" ? { variation: "destructive" as const } : {})}
            loading={busy}
            onClick={() => (how === "void" ? voidIt.mutate(i.id, after) : noCharge.mutate(i.id, after))}
          >
            {how === "void" ? "Void invoice" : "No charge"}
          </Button>
        </>
      }
    >
      <p>
        {how === "void"
          ? `${i.clientName} no longer owes ${moneyExact(i.balanceCents)}. The pay link stops working. Use it for an invoice made by mistake.`
          : `${i.clientName} owes nothing for this work. It stays on their history as no charge.`}
      </p>
    </Modal>
  );
}

/**
 * Who worked an invoice typed by hand (no visits), so payroll pays them (question 43). Invoices from visits get their team
 * from each visit.
 */
function WhoWorked({ i }: { i: InvoiceDetail }) {
  const { can, company } = useSession();
  const people = usePeople(company.id);
  const update = useUpdateInvoice();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const editable = can("invoice.update") && i.status !== "void" && i.status !== "no_charge";
  const team = (people.data ?? []).filter((p) => p.active).map((p) => ({ id: p.id, name: p.name, hint: p.role }));
  return (
    <Card
      title="Who worked it"
      action={
        editable ? (
          <Button type="tertiary" size="small" icon="person" onClick={() => setOpen(true)}>
            {i.crew.length ? "Change" : "Set team"}
          </Button>
        ) : null
      }
    >
      <CrewChips
        crew={i.crew.map((c) => ({ ...c, color: null, photo: null }))}
        none={i.nobodyWorked ? "Nobody worked this" : "Nobody yet"}
      />
      {editable && i.crew.length === 0 ? (
        <label className="check-row">
          <input
            type="checkbox"
            checked={i.nobodyWorked}
            onChange={(e) => update.mutate({ id: i.id, patch: { nobodyWorked: e.target.checked } })}
          />
          Nobody worked this (a fee or a product). Payroll pays nobody and it leaves No crew yet.
        </label>
      ) : null}
      <p className="muted">
        {i.crew.length
          ? "Payroll pays them for this invoice once it is paid."
          : "This invoice has no visit, so payroll pays nobody for it until you set who worked it."}
      </p>
      <TeamSheet
        open={open}
        onClose={() => setOpen(false)}
        people={team}
        selected={i.crew.map((c) => c.id)}
        repeating={false}
        saving={update.isPending}
        onSave={(ids) =>
          update.mutate(
            { id: i.id, patch: { crew: ids } },
            {
              onSuccess: () => {
                setOpen(false);
                toast("Team saved");
              },
              onError: (e) => toast(errText(e), { kind: "error" }),
            },
          )
        }
      />
    </Card>
  );
}

function PoNumber({ i }: { i: InvoiceDetail }) {
  const { can } = useSession();
  const update = useUpdateInvoice();
  const toast = useToast();
  const [po, setPo] = useState(i.poNumber ?? "");
  if (!can("invoice.update") || i.status === "paid") return i.poNumber ?? <span className="muted">None</span>;
  return (
    <form
      className="row"
      onSubmit={(e) => {
        e.preventDefault();
        update.mutate(
          { id: i.id, patch: { poNumber: po.trim() || null } },
          { onSuccess: () => toast("PO number saved"), onError: (er) => toast(errText(er), { kind: "error" }) },
        );
      }}
    >
      <input
        className="input-plain"
        value={po}
        placeholder="Add one"
        aria-label="PO number"
        onChange={(e) => setPo(e.target.value)}
      />
      {po !== (i.poNumber ?? "") ? (
        <Button size="small" submit loading={update.isPending}>
          Save
        </Button>
      ) : null}
    </form>
  );
}

/** Shows the exact message and who gets it before anything goes out. */
/** Send receipt (paid invoices): a text, or an email with the receipt PDF, to the client's own number or email. */
export function ReceiptBox({ i, onClose }: { i: InvoiceDetail; onClose: () => void }) {
  const [via, setVia] = useState<Via>(i.client.email ? "email" : "text");
  const [picked, setPicked] = useState<string | null>(null);
  const preview = useSendPreview(i.id, via, true);
  const receipt = useSendReceipt();
  const toast = useToast();
  const testMode = useTestMode(useSession().company.id);
  const choices = preview.data?.choices ?? [];
  const to = picked && choices.includes(picked) ? picked : (choices[0] ?? null);
  return (
    <Modal
      title="Send receipt"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            icon="mail"
            disabled={!to}
            loading={receipt.isPending}
            onClick={() =>
              to &&
              receipt.mutate(
                { id: i.id, via, to },
                {
                  onSuccess: () => {
                    toast(testMode ? `Receipt saved for ${to}. Test mode, so it waits in the outbox.` : `Receipt sent to ${to}.`);
                    onClose();
                  },
                  onError: (e) => toast(errText(e), { kind: "error" }),
                },
              )
            }
          >
            Send receipt
          </Button>
        </>
      }
    >
      <div className="stack">
        <Choice<Via>
          label="Send by"
          value={via}
          onChange={(v) => {
            setVia(v);
            setPicked(null);
          }}
          options={[
            { value: "text", label: "Text" },
            { value: "email", label: "Email, with the PDF" },
          ]}
        />
        {choices.length > 1 ? (
          <Choice<string>
            label="Send to"
            value={to ?? ""}
            onChange={setPicked}
            options={choices.map((c) => ({ value: c, label: c }))}
          />
        ) : (
          <div className="field">
            <span className="label">Send to</span>
            <b>{to ?? `${i.client.name} has no ${via === "text" ? "phone number" : "email"}.`}</b>
          </div>
        )}
        <p className="muted small">It says the amount, the day it was paid and how, with the link to the invoice.</p>
      </div>
    </Modal>
  );
}

function SendBox({ i, via: firstVia, open, onClose }: { i: InvoiceDetail; via: Via; open: boolean; onClose: () => void }) {
  const [via, setVia] = useState<Via>(firstVia);
  const [picked, setPicked] = useState<string | null>(null);
  const [later, setLater] = useState("");
  const [cc, setCc] = useState<string[]>([]);
  const [copyMe, setCopyMe] = useState(false);
  const [pdf, setPdf] = useState<boolean | null>(null);
  const testMode = useTestMode(useSession().company.id);
  const preview = useSendPreview(i.id, via, open);
  const send = useSendInvoice();
  const toast = useToast();
  const p = preview.data;
  const to = picked && p?.choices.includes(picked) ? picked : (p?.to ?? null);
  const go = () => {
    if (!to) return;
    send.mutate(
      {
        id: i.id,
        via,
        to,
        ...(later ? { sendAt: new Date(later).toISOString() } : {}),
        cc: cc.filter((x) => x !== to),
        copyMe,
        pdf: pdf ?? p?.pdf ?? false,
      },
      {
        onSuccess: () => {
          toast(
            later
              ? `Invoice will go to ${to} at the time you picked.`
              : testMode
                ? `Invoice saved for ${to}. Test mode, so it waits in the outbox.`
                : `Invoice sent to ${to}.`,
          );
          onClose();
        },
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  };
  return (
    <Modal
      title={i.state === "to_check" ? "Confirm and send" : "Resend invoice"}
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button icon="mail" disabled={!to} loading={send.isPending} onClick={go}>
            Send {via === "text" ? "text" : "email"}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Choice<Via>
          label="Send by"
          value={via}
          onChange={(v) => {
            setVia(v);
            setPicked(null);
          }}
          options={[
            { value: "text", label: "Text" },
            { value: "email", label: "Email" },
          ]}
        />
        {preview.error ? <LoadError error={preview.error} /> : null}
        {p ? (
          <>
            {p.choices.length > 1 ? (
              <Choice<string>
                label="Send to"
                value={to ?? ""}
                onChange={setPicked}
                options={p.choices.map((c) => ({ value: c, label: c }))}
              />
            ) : (
              <div className="field">
                <span className="label">Send to</span>
                <b>
                  {to ?? `${i.client.name} has no ${via === "text" ? "phone number" : "email"}. Add one on the client first.`}
                </b>
              </div>
            )}
            {p.warnings.map((w) => (
              <Banner key={w} type="warning">
                {w}
              </Banner>
            ))}
            {p.subject ? (
              <div className="field">
                <span className="label">Subject</span>
                <span>{p.subject}</span>
              </div>
            ) : null}
            <div className="field">
              <span className="label">The message</span>
              <div className="message-preview">{p.body}</div>
            </div>
            {via === "email" ? (
              <div className="stack">
                {p.choices
                  .filter((c) => c !== to)
                  .map((c) => (
                    <label key={c} className="check-row">
                      <input
                        type="checkbox"
                        checked={cc.includes(c)}
                        onChange={(e) => setCc(e.target.checked ? [...cc, c] : cc.filter((x) => x !== c))}
                      />
                      <span>Copy to {c}</span>
                    </label>
                  ))}
                <label className="check-row">
                  <input type="checkbox" checked={pdf ?? p.pdf} onChange={(e) => setPdf(e.target.checked)} />
                  <span>Attach a PDF copy</span>
                </label>
                <label className="check-row">
                  <input type="checkbox" checked={copyMe} onChange={(e) => setCopyMe(e.target.checked)} />
                  <span>Send me a copy</span>
                </label>
              </div>
            ) : null}
            {testMode ? (
              <p className="muted small">Texts and emails are in test mode. This goes to the outbox and is not sent.</p>
            ) : null}
            {i.status === "to_check" || i.status === "scheduled" ? (
              <InputText
                label="Send later (leave empty to send now)"
                type="datetime-local"
                value={later}
                onChange={(e) => setLater(e.target.value)}
              />
            ) : null}
          </>
        ) : (
          <Skeleton rows={3} />
        )}
      </div>
    </Modal>
  );
}

/**
 * Cash, check or card (owner, Nov 1: "also add manual adding card for a customer in case they have no payment saved").
 * Card charges the balance on the saved card, after naming the client, card and amount. With no saved card the office
 * types the one the client reads out (Take card by phone, saved like theirs), then charges it here.
 */
function CollectPayment({ i, open, onClose }: { i: InvoiceDetail; open: boolean; onClose: () => void }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const record = useRecordPayment();
  const charge = useChargeCard();
  const toast = useToast();
  const [method, setMethod] = useState<"cash" | "check" | "card">(
    i.crewCollected === "check" || i.crewCollected === "company" ? "check" : "cash",
  );
  const [amount, setAmount] = useState("");
  const [check, setCheck] = useState("");
  const [key, setKey] = useState(newKey);
  const [taking, setTaking] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const card = i.client.card;
  const mayCard = can("card.charge") && i.balanceCents >= 50;
  const shown = amount || (i.balanceCents / 100).toFixed(2);
  const done = (words: string) => {
    toast(words);
    setKey(newKey());
    setAmount("");
    setCheck("");
    setErr(null);
    onClose();
  };
  const go = () => {
    if (method === "card") {
      if (!card) {
        setTaking(true);
        return;
      }
      charge.mutate(
        { id: i.id, key, amountCents: i.balanceCents, last4: card.last4 },
        { onSuccess: () => done(`${moneyExact(i.balanceCents)} charged`), onError: (e) => setErr(errText(e)) },
      );
      return;
    }
    const cents = dollars(shown);
    if (cents === null) {
      setErr("Type the amount you got.");
      return;
    }
    if (cents > i.balanceCents) {
      setErr(`That is more than the balance of ${moneyExact(i.balanceCents)}.`);
      return;
    }
    record.mutate(
      { id: i.id, key, method, amountCents: cents, checkNumber: method === "check" ? check.trim() || null : null },
      { onSuccess: () => done(`${moneyExact(cents)} ${method} recorded`), onError: (e) => setErr(errText(e)) },
    );
  };
  if (open && taking)
    return (
      <PhoneCard
        c={i.client}
        onClose={() => setTaking(false)}
        onSaved={() => {
          // The invoice shows the new card, then Charge asks before any money moves.
          void qc.invalidateQueries({ queryKey: ["invoice", i.id] }).then(() => setTaking(false));
        }}
      />
    );
  return (
    <Modal
      title="Collect payment"
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={record.isPending || charge.isPending} onClick={go}>
            {method === "card" ? (card ? `Yes, charge ${moneyExact(i.balanceCents)}` : "Add card") : "Record payment"}
          </Button>
        </>
      }
    >
      <div className="stack">
        <Choice<"cash" | "check" | "card">
          label="Paid by"
          value={method}
          onChange={(m) => {
            setMethod(m);
            setErr(null);
          }}
          options={[
            { value: "cash", label: "Cash" },
            { value: "check", label: "Check" },
            ...(mayCard ? [{ value: "card" as const, label: "Card" }] : []),
          ]}
        />
        {method === "card" ? (
          card ? (
            <>
              <p>
                <b>
                  {chargeQuestion({
                    client: i.client.name,
                    cardBrand: card.brand,
                    last4: card.last4,
                    amountCents: i.balanceCents,
                  })}
                </b>
              </p>
              <p className="muted small">
                The card is charged right away for the whole balance. Wrong card?{" "}
                <button type="button" className="linkish" onClick={() => setTaking(true)}>
                  Add a different card
                </button>
              </p>
            </>
          ) : (
            <p className="muted small">
              {i.client.name} has no saved card. Add card opens a secure Stripe box. Type the card as they read it to you. It is
              saved, then you charge {moneyExact(i.balanceCents)} here.
            </p>
          )
        ) : (
          <>
            <InputText
              label="Amount"
              prefix="$"
              inputMode="decimal"
              value={shown}
              error={err}
              onChange={(e) => setAmount(e.target.value)}
            />
            {method === "check" ? (
              <InputText label="Check number" value={check} onChange={(e) => setCheck(e.target.value)} />
            ) : null}
            {mayCard ? null : <p className="muted small">Card payments come in by themselves from the pay page.</p>}
          </>
        )}
        {method === "card" && err ? <Banner type="error">{err}</Banner> : null}
      </div>
    </Modal>
  );
}

/** Changes the lines. On a paid invoice, a higher total opens it for the difference; a lower one asks Credit or Don't credit. */
function EditLines({ i, open, onClose }: { i: InvoiceDetail; open: boolean; onClose: () => void }) {
  const [lines, setLines] = useState<DraftLine[]>(() =>
    i.lines.map((l) =>
      draftLine({ description: l.description, quantity: l.quantity, unitPriceCents: l.unitPriceCents, frequency: "every" }),
    ),
  );
  const [choice, setChoice] = useState<"credit" | "no_credit" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const update = useUpdateInvoice();
  const editPaid = useEditPaid();
  const toast = useToast();
  const paid = i.paidCents;
  const total = draftTotal(lines) + i.tipCents;
  const lower = paid > 0 && total < paid;
  const go = () => {
    const out = linesOut(lines);
    if (typeof out === "string") {
      setErr(out);
      return;
    }
    if (!out.length) {
      setErr("An invoice needs a line.");
      return;
    }
    const body: InvoiceLineIn[] = out.map((l) => ({
      description: l.description,
      quantity: l.quantity,
      unitPriceCents: l.unitPriceCents,
    }));
    if (lower && !choice) {
      setErr("Pick Credit or Don't credit for the money already paid.");
      return;
    }
    const after = {
      onSuccess: () => {
        toast(
          lower
            ? choice === "credit"
              ? "Saved. The difference is now account credit."
              : "Saved. No credit given."
            : "Line items saved",
        );
        onClose();
      },
      onError: (e: unknown) => setErr(errText(e)),
    };
    if (paid > 0) editPaid.mutate({ id: i.id, body: { lines: body, ...(lower && choice ? { choice } : {}) } }, after);
    else update.mutate({ id: i.id, patch: { lines: body } }, after);
  };
  return (
    <Modal
      title="Edit line items"
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={update.isPending || editPaid.isPending} onClick={go}>
            Save
          </Button>
        </>
      }
    >
      <div className="stack">
        {paid > 0 ? (
          <Banner type="notice">
            {moneyExact(paid)} is already paid. A higher total asks the client for the difference only.
          </Banner>
        ) : null}
        <LinesEditor lines={lines} onChange={setLines} />
        {lower ? (
          <Choice<"credit" | "no_credit">
            label={`The new total is ${moneyExact(paid - total)} less than what was paid`}
            value={choice ?? ("" as "credit")}
            onChange={setChoice}
            options={[
              { value: "credit", label: "Credit", hint: "Keep it as account credit for the next invoice" },
              { value: "no_credit", label: "Don't credit", hint: "Keep the money. A note is added" },
            ]}
          />
        ) : null}
        {err ? <Banner type="error">{err}</Banner> : null}
      </div>
    </Modal>
  );
}

/** Owner only. Names the client, card and amount, and asks before charging. */
function ChargeCard({ i, open, onClose }: { i: InvoiceDetail; open: boolean; onClose: () => void }) {
  const charge = useChargeCard();
  const toast = useToast();
  const [key, setKey] = useState(newKey);
  const card = i.client.card;
  if (!card) return null;
  return (
    <Modal
      title="Charge saved card"
      open={open}
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={charge.isPending}
            onClick={() =>
              charge.mutate(
                { id: i.id, key, amountCents: i.balanceCents, last4: card.last4 },
                {
                  onSuccess: () => {
                    toast(`${moneyExact(i.balanceCents)} charged`);
                    setKey(newKey());
                    onClose();
                  },
                  onError: (e) => toast(errText(e), { kind: "error" }),
                },
              )
            }
          >
            Yes, charge {moneyExact(i.balanceCents)}
          </Button>
        </>
      }
    >
      <p>
        <b>{chargeQuestion({ client: i.client.name, cardBrand: card.brand, last4: card.last4, amountCents: i.balanceCents })}</b>
      </p>
      <p className="muted">
        {i.status === "to_check"
          ? "The invoice gets its number and is marked paid. The receipt goes out like any card payment."
          : "The card is charged right away."}
      </p>
    </Modal>
  );
}

/** Give back part or all of a payment (owners only, question 33). */
export function RefundBox({ i, paymentId, onClose }: { i: InvoiceDetail; paymentId: string; onClose: () => void }) {
  const refund = useRefund();
  const toast = useToast();
  const p = i.payments.find((x) => x.id === paymentId);
  const [amount, setAmount] = useState(p ? (p.amountCents / 100).toFixed(2) : "");
  const [reason, setReason] = useState("");
  const [keepOwed, setKeepOwed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cents = Math.round(Number(amount.replace(/[$,\s]/g, "")) * 100);
  return (
    <Modal
      title="Refund"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={refund.isPending}
            disabled={!(cents > 0) || !reason.trim()}
            onClick={() =>
              refund.mutate(
                { id: i.id, paymentId, amountCents: cents, reason: reason.trim(), keepOwed },
                {
                  onSuccess: () => {
                    toast("Refund made");
                    onClose();
                  },
                  onError: (e) => setError(e instanceof Error ? e.message : "That did not go through."),
                },
              )
            }
          >
            Give back {cents > 0 ? moneyExact(cents) : ""}
          </Button>
        </>
      }
    >
      <p className="muted">
        {p?.method === "card" ? "The money goes back on the client's card." : "Write down cash or a check you gave back."}
      </p>
      <InputText label="Amount" prefix="$" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
      <InputText label="Why" value={reason} onChange={(e) => setReason(e.target.value)} />
      <label className="check-row">
        <input type="checkbox" checked={keepOwed} onChange={(e) => setKeepOwed(e.target.checked)} />
        They still owe it (a payment made by mistake). Leave off to lower the invoice too.
      </label>
      {error ? <Banner type="error">{error}</Banner> : null}
    </Modal>
  );
}

/**
 * An invoice typed by hand (question 39): a fee, a product, work done without a visit. The old Steward's form (client, lines,
 * who did the work for pay, a message) with Jobber's Save or Save and send. Saved, it waits in To check.
 */
function NewInvoice({
  clientId: firstClient,
  onClose,
  onMade,
}: {
  clientId: string;
  onClose: () => void;
  onMade: (id: string, send: boolean) => void;
}) {
  const { company } = useSession();
  const clients = useClients(company.id, { sort: "last_activity" });
  const people = usePeople(company.id);
  const make = useNewInvoice();
  const [clientId, setClientId] = useState(firstClient);
  const [lines, setLines] = useState<DraftLine[]>(() => [draftLine()]);
  const [crew, setCrew] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const team = (people.data ?? []).filter((p) => p.active);
  const go = (send: boolean) => {
    if (!clientId) {
      setError("Pick the client.");
      return;
    }
    const out = linesOut(lines);
    if (typeof out === "string") {
      setError(out);
      return;
    }
    if (!out.length) {
      setError("Add a line with what it is for and the price.");
      return;
    }
    setSending(send);
    make.mutate(
      {
        clientId,
        lines: out.map((l) => ({ description: l.description, quantity: l.quantity, unitPriceCents: l.unitPriceCents })),
        ...(note.trim() ? { noteToClient: note.trim() } : {}),
        ...(crew.length ? { crew } : {}),
      },
      {
        onSuccess: (d) => onMade(d.id, send),
        onError: (e) => setError(errText(e, "That did not save.")),
      },
    );
  };
  return (
    <Modal
      title="New invoice"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="secondary" loading={make.isPending && !sending} onClick={() => go(false)}>
            Save invoice
          </Button>
          <Button icon="mail" loading={make.isPending && sending} onClick={() => go(true)}>
            Save and send
          </Button>
        </>
      }
    >
      <div className="stack">
        <Combobox
          label="Client"
          value={clientId}
          onChange={setClientId}
          options={(clients.data?.items ?? []).map((c) => ({ value: c.id, label: c.name }))}
        />
        <LinesEditor lines={lines} onChange={setLines} />
        {team.length ? (
          <div className="field">
            <span className="label">Who did the work (for pay)</span>
            <div className="chips">
              {team.map((p) => (
                <label key={p.id} className="check-row">
                  <input
                    type="checkbox"
                    checked={crew.includes(p.id)}
                    onChange={(e) => setCrew(e.target.checked ? [...crew, p.id] : crew.filter((x) => x !== p.id))}
                  />
                  {p.name}
                </label>
              ))}
            </div>
            <span className="muted small">Leave everyone off for a fee, a product, or work that was already paid.</span>
          </div>
        ) : null}
        <label className="field">
          <span className="label">Message to the client (optional)</span>
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <p className="muted small">Save invoice puts it in To check and sends nothing. Save and send shows the message first.</p>
        {error ? <Banner type="error">{error}</Banner> : null}
      </div>
    </Modal>
  );
}
